import { useState } from "react";
import "./ModalStyles.css";
import { updateSolicitacaoImpressaoApi } from "../../Api";
import { notify } from "../Notify";

const UFS = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB',
    'PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

function maskCep(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
}

const ModalDadosFisica = ({ impressaoId, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [cepLoading, setCepLoading] = useState(false);
    const [endereco, setEndereco] = useState({
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
    });
    // Step 2: confirmação. Depois de validar o formulário, abrimos um modo
    // "revisão" no mesmo modal pedindo pra o aluno confirmar o endereço
    // antes de salvar. Depois de salvo, ele NÃO pode editar pelo app —
    // tem que pedir pro suporte; daí a fricção intencional aqui.
    const [confirmStep, setConfirmStep] = useState(false);
    const [confirmado, setConfirmado] = useState(false);

    const buscarCep = async (cepMasked) => {
        const digits = String(cepMasked || '').replace(/\D/g, '');
        if (digits.length !== 8) return;
        setCepLoading(true);
        try {
            const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            const data = await res.json();
            if (data?.erro) {
                notify('CEP não encontrado. Preencha os campos manualmente.', 'warning');
                return;
            }
            setEndereco(prev => ({
                ...prev,
                logradouro: data.logradouro || prev.logradouro,
                bairro: data.bairro || prev.bairro,
                cidade: data.localidade || prev.cidade,
                estado: (data.uf || prev.estado || '').toUpperCase(),
            }));
        } catch {
            notify('Não foi possível consultar o CEP agora. Preencha manualmente.', 'warning');
        } finally {
            setCepLoading(false);
        }
    };

    const handleCepChange = (e) => {
        const masked = maskCep(e.target.value);
        setEndereco(prev => ({ ...prev, cep: masked }));
        if (masked.replace(/\D/g, '').length === 8) buscarCep(masked);
    };

    // Etapa 1: valida o formulário e abre o passo de confirmação. Não chama
    // a API ainda — só após o aluno marcar o checkbox e clicar "Confirmar".
    const abrirConfirmacao = () => {
        const cepDigits = endereco.cep.replace(/\D/g, '');
        if (cepDigits.length !== 8) {
            notify('Informe um CEP válido (8 dígitos).', 'warning');
            return;
        }
        if (!endereco.logradouro.trim() || !endereco.numero.trim() ||
            !endereco.bairro.trim() || !endereco.cidade.trim() ||
            !endereco.estado.trim()) {
            notify('Preencha todos os campos obrigatórios.', 'warning');
            return;
        }
        setConfirmado(false); // sempre começa desmarcado
        setConfirmStep(true);
    };

    const voltarParaEditar = () => {
        setConfirmStep(false);
        setConfirmado(false);
    };

    // Etapa 2: o aluno confirmou — agora sim envia.
    const confirmarEEnviar = async () => {
        if (!confirmado) return;
        const cepDigits = endereco.cep.replace(/\D/g, '');
        setLoading(true);
        try {
            await updateSolicitacaoImpressaoApi({
                impressaoId,
                endereco: {
                    cep: cepDigits,
                    logradouro: endereco.logradouro.trim(),
                    numero: endereco.numero.trim(),
                    complemento: endereco.complemento.trim() || null,
                    bairro: endereco.bairro.trim(),
                    cidade: endereco.cidade.trim(),
                    estado: endereco.estado.trim().toUpperCase(),
                },
            });
            notify('Endereço de entrega salvo!', 'success');
            onSuccess();
        } catch (err) {
            notify(
                err.response?.data?.message || 'Erro ao salvar endereço. Tente novamente.',
                'error',
            );
        } finally {
            setLoading(false);
        }
    };

    if (confirmStep) {
        const cepFmt = maskCep(endereco.cep);
        const linhaPrincipal = [
            endereco.logradouro.trim(),
            endereco.numero.trim() && `nº ${endereco.numero.trim()}`,
        ].filter(Boolean).join(', ');
        return (
            <div className="modal-overlay">
                <div className="modal-container glass">
                    <div className="modal-header">
                        <h2>Confirme seu endereço</h2>
                        <p>Confira com atenção — depois de confirmar, o endereço NÃO poderá ser alterado por aqui.</p>
                    </div>

                    <div className="modal-body">
                        <div style={confirmStyles.aviso}>
                            ⚠️ <strong>Atenção:</strong> a carteirinha física será postada exatamente
                            para o endereço abaixo. Após confirmar, ele fica trancado — em caso de
                            erro você só conseguirá ajustar pelo suporte.
                        </div>

                        <div style={confirmStyles.resumo}>
                            <Linha label="CEP" valor={cepFmt} />
                            <Linha label="Logradouro" valor={linhaPrincipal} />
                            {endereco.complemento.trim() && (
                                <Linha label="Complemento" valor={endereco.complemento.trim()} />
                            )}
                            <Linha label="Bairro" valor={endereco.bairro.trim()} />
                            <Linha
                                label="Cidade / UF"
                                valor={`${endereco.cidade.trim()} - ${endereco.estado.trim().toUpperCase()}`}
                            />
                        </div>

                        <label style={confirmStyles.checkboxRow}>
                            <input
                                type="checkbox"
                                checked={confirmado}
                                onChange={(e) => setConfirmado(e.target.checked)}
                                disabled={loading}
                                style={confirmStyles.checkbox}
                            />
                            <span>
                                Confirmo que os dados acima estão corretos e estou ciente de que
                                não poderei alterá-los depois.
                            </span>
                        </label>
                    </div>

                    <div className="modal-footer" style={confirmStyles.footer}>
                        <button
                            type="button"
                            onClick={voltarParaEditar}
                            disabled={loading}
                            style={confirmStyles.btnVoltar}
                        >
                            ← Voltar e editar
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={confirmarEEnviar}
                            disabled={loading || !confirmado}
                            style={{ ...(confirmado ? {} : confirmStyles.btnDisabled), flex: 1 }}
                        >
                            {loading ? 'Salvando…' : 'Confirmar endereço'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay">
            <div className="modal-container glass">
                <div className="modal-header">
                    <h2>Endereço de Entrega</h2>
                    <p>Precisamos do seu endereço para o envio da carteirinha física.</p>
                </div>

                <div className="modal-body">
                    <div className="input-group">
                        <label>CEP</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="00000-000"
                            value={endereco.cep}
                            onChange={handleCepChange}
                            maxLength={9}
                            disabled={loading}
                        />
                        {cepLoading && (
                            <small style={{ color: '#11A697', marginTop: 4 }}>
                                Buscando endereço…
                            </small>
                        )}
                    </div>

                    <div className="input-group">
                        <label>Logradouro</label>
                        <input
                            type="text"
                            placeholder="Rua, avenida, travessa…"
                            value={endereco.logradouro}
                            onChange={e => setEndereco({ ...endereco, logradouro: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="input-group">
                        <label>Número</label>
                        <input
                            type="text"
                            placeholder="Ex: 123 ou s/n"
                            value={endereco.numero}
                            onChange={e => setEndereco({ ...endereco, numero: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="input-group">
                        <label>Complemento <span style={{ color: '#888', fontWeight: 400 }}>(opcional)</span></label>
                        <input
                            type="text"
                            placeholder="Apto, bloco, casa…"
                            value={endereco.complemento}
                            onChange={e => setEndereco({ ...endereco, complemento: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="input-group">
                        <label>Bairro</label>
                        <input
                            type="text"
                            placeholder="Bairro"
                            value={endereco.bairro}
                            onChange={e => setEndereco({ ...endereco, bairro: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="input-group">
                        <label>Cidade</label>
                        <input
                            type="text"
                            placeholder="Cidade"
                            value={endereco.cidade}
                            onChange={e => setEndereco({ ...endereco, cidade: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="input-group">
                        <label>Estado</label>
                        <select
                            value={endereco.estado}
                            onChange={e => setEndereco({ ...endereco, estado: e.target.value })}
                            disabled={loading}
                        >
                            <option value="">Selecione a UF</option>
                            {UFS.map(uf => (
                                <option key={uf} value={uf}>{uf}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        className="btn-primary"
                        onClick={abrirConfirmacao}
                        disabled={loading}
                    >
                        Continuar →
                    </button>
                </div>
            </div>
        </div>
    );
};

function Linha({ label, valor }) {
    return (
        <div style={confirmStyles.linha}>
            <div style={confirmStyles.linhaLabel}>{label}</div>
            <div style={confirmStyles.linhaValor}>{valor || '—'}</div>
        </div>
    );
}

const confirmStyles = {
    aviso: {
        background: '#FEF3C7',
        border: '1px solid #FCD34D',
        borderLeft: '4px solid #F59E0B',
        borderRadius: 10,
        padding: '12px 14px',
        color: '#78350F',
        fontSize: '0.88rem',
        lineHeight: 1.5,
        marginBottom: 16,
    },
    resumo: {
        background: '#F8FAFC',
        border: '1px solid #E2E8F0',
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    linha: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    linhaLabel: {
        fontSize: '0.7rem',
        fontWeight: 700,
        color: '#94A3B8',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 2,
    },
    linhaValor: {
        fontSize: '0.92rem',
        color: '#0F172A',
        fontWeight: 600,
        wordBreak: 'break-word',
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 10,
        padding: '12px 14px',
        cursor: 'pointer',
        fontSize: '0.88rem',
        color: '#334155',
        lineHeight: 1.45,
        userSelect: 'none',
    },
    checkbox: {
        marginTop: 3,
        width: 18,
        height: 18,
        accentColor: '#059669',
        flexShrink: 0,
        cursor: 'pointer',
    },
    footer: { display: 'flex', gap: 10 },
    btnVoltar: {
        background: '#FFFFFF',
        color: '#475569',
        border: '1px solid #CBD5E1',
        borderRadius: 10,
        padding: '0 16px',
        fontSize: '0.88rem',
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    btnDisabled: {
        opacity: 0.55,
        cursor: 'not-allowed',
    },
};

export default ModalDadosFisica;
